// ============================================================
// NOTIFICATION TRANSLATOR - Translate notification content dynamically
// ============================================================

/**
 * Normalizes floating point numbers in notification text to 2 decimal places.
 * Example: -5.6000 -> -5.60, 175.0000 -> 175.00
 * Keeps integers unchanged.
 */
function normalizeNotificationDecimals(text) {
  if (!text) return text;
  return String(text).replace(/-?\d+\.\d{3,}/g, (match) => {
    const value = Number(match);
    if (!Number.isFinite(value)) return match;
    return value.toFixed(2);
  });
}

/**
 * Translates notification title and description based on patterns
 * @param {string} text - The notification title or description
 * @param {Function} t - Translation function from useTranslate hook
 * @param {string} language - Current language code
 * @returns {string} - Translated text
 */
export function translateNotificationText(text, t, language) {
  if (!text) return text;
  text = normalizeNotificationDecimals(text);
  if (language === 'en') return text; // Return normalized original for English
  
  // Helper to replace template variables like {{item}}, {{date}}, etc.
  const replaceVariables = (template, matches) => {
    let result = t(template);
    Object.keys(matches).forEach(key => {
      result = result.replace(`{{${key}}}`, matches[key]);
    });
    return result;
  };

  // Pattern matching for different notification types

  // Wastage Alert
  if (text.startsWith('Wastage Alert: Trend Up')) {
    const daysMatch = text.match(/Last (\d+) Day\(s\)/);
    const days = daysMatch ? daysMatch[1] : '7';
    return replaceVariables('notifications.content.wastageAlertTrendUp', { days });
  }
  
  if (text.includes('wastage cost trend is up by')) {
    const percentMatch = text.match(/up by ([\d.—]+)%/);
    const daysMatch = text.match(/last (\d+) day\(s\)/);
    const percent = percentMatch ? percentMatch[1] : '—';
    const days = daysMatch ? daysMatch[1] : '7';
    return replaceVariables('notifications.content.wastageAlertDesc', { percent, days });
  }

  // Reminder: Daily Wastage Report
  if (text.startsWith('Reminder: Daily Wastage Report Due')) {
    const dateMatch = text.match(/— (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.reminderWastageReport', { date });
  }
  
  if (text.includes('daily wastage report for') && text.includes('has not been submitted')) {
    const dateMatch = text.match(/for (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.reminderWastageDesc', { date });
  }

  // Reminder: Daily Stock Out Report
  if (text.startsWith('Reminder: Daily Stock Out Report Due')) {
    const dateMatch = text.match(/— (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.reminderStockOutReport', { date });
  }
  
  if (text.includes('daily stock usage report for') && text.includes('has not been submitted')) {
    const dateMatch = text.match(/for (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.reminderStockOutDesc', { date });
  }

  // End of Day Report Ready
  if (text.startsWith('End of Day Report Ready')) {
    const dateMatch = text.match(/— (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.endOfDayReportReady', { date });
  }
  
  if (text.includes('daily report for') && text.includes('is ready')) {
    const dateMatch = text.match(/for (\d{4}-\d{2}-\d{2})/);
    const date = dateMatch ? dateMatch[1] : '';
    return replaceVariables('notifications.content.endOfDayDesc', { date });
  }

  // Restocked
  if (text.startsWith('Restocked:')) {
    const item = text.replace('Restocked:', '').trim();
    return replaceVariables('notifications.content.restocked', { item });
  }
  
  if (text.includes('Stock for') && text.includes('is now')) {
    const match = text.match(/Stock for (.+?) is now ([\d.]+) (.+?),/);
    if (match) {
      const [, item, quantity, unit] = match;
      return replaceVariables('notifications.content.restockedDesc', { item, quantity, unit });
    }
  }

  // Recipe Updated
  if (text.startsWith('Recipe Updated:')) {
    const recipe = text.replace('Recipe Updated:', '').trim();
    return replaceVariables('notifications.content.recipeUpdated', { recipe });
  }
  
  if (text.includes('recipe for') && text.includes('was updated')) {
    const match = text.match(/recipe for (.+?) was updated\. (\d+) ingredients/);
    if (match) {
      const [, recipe, count] = match;
      return replaceVariables('notifications.content.recipeUpdatedDesc', { recipe, count });
    }
  }

  // New Inventory Item Created
  if (text.startsWith('New Inventory Item Created:')) {
    const item = text.replace('New Inventory Item Created:', '').trim();
    return replaceVariables('notifications.content.newInventoryItem', { item });
  }
  
  if (text.includes('created a new inventory item')) {
    const match = text.match(/item "(.+?)" when updating the recipe (.+?)\. Standard unit: (.+?)\./);
    if (match) {
      const [, item, recipe, unit] = match;
      return replaceVariables('notifications.content.newInventoryItemDesc', { item, recipe, unit });
    }
  }

  // Zero-Cost Recipe
  if (text.startsWith('Zero-Cost Recipe:')) {
    const recipe = text.replace('Zero-Cost Recipe:', '').trim();
    return replaceVariables('notifications.content.zeroCostRecipe', { recipe });
  }
  
  if (text.includes('has no cost data')) {
    const match = text.match(/recipe for (.+?) has no cost data/);
    if (match) {
      const recipe = match[1];
      return replaceVariables('notifications.content.zeroCostRecipeDesc', { recipe });
    }
  }

  // Unmapped Ingredient Unit
  if (text.startsWith('Unmapped Ingredient Unit:')) {
    const ingredient = text.replace('Unmapped Ingredient Unit:', '').trim();
    return replaceVariables('notifications.content.unmappedIngredient', { ingredient });
  }
  
  if (text.includes('uses unit') && text.includes('but the inventory item uses')) {
    const match = text.match(/ingredient (.+?) in recipe (.+?) uses unit "(.+?)" but the inventory item uses "(.+?)"/);
    if (match) {
      const [, ingredient, recipe, recipeUnit, inventoryUnit] = match;
      return replaceVariables('notifications.content.unmappedIngredientDesc', { 
        ingredient, 
        recipe, 
        recipeUnit, 
        inventoryUnit 
      });
    }
  }

  // If no pattern matches, return original text
  return text;
}

/**
 * Translates notification tag/type
 * @param {string} type - Notification type
 * @param {Function} t - Translation function
 * @returns {string} - Translated type
 */
export function translateNotificationType(type, t) {
  const typeMap = {
    'Low Stock': 'notifications.lowStock',
    'Info': 'notifications.info',
    'Warning': 'notifications.warning',
    'Alert': 'notifications.alert',
    'Success': 'notifications.success',
    'Restocked': 'notifications.success',
    'Inventory': 'notifications.inventory',
    'Stock': 'notifications.stock',
    'Recipe': 'notifications.recipe'
  };
  
  return typeMap[type] ? t(typeMap[type]) : type;
}
